import localFont from 'next/font/local';

export const scale = (size: number): number => size;

const bold = localFont({ src: '../public/fonts/IRANSans_Bold.ttf' });
const body = localFont({ src: '../public/fonts/IRANSans_Medium.ttf' });
const regular = localFont({ src: '../public/fonts/IRANSans_Regular.ttf' });

const theme = {
  radii: {
    xs: '4px',
    sm: '6px',
    md: '8px',
    lg: '10px',
    xl: '12px',
    xxl: '32px',
    full: '1000px',
  },
  fonts: {
    body: body.style.fontFamily,
    bold: bold.style.fontFamily,
    regular: regular.style.fontFamily,
  },
  fontSizes: [
    '1rem', // 0
    '1.1rem', // 1
    '1.2rem', // 2
    '1.3rem', // 3
    '1.4rem', // 4
    '1.5rem', // 5
    '1.6rem', // 6
    '1.7rem', // 7
    '1.8rem', // 8
    '1.9rem', // 9
    '2rem', // 10
    '2.1rem', // 11
    '2.2rem', // 12
    '2.3rem', // 13
    '2.4rem', // 14
    '2.5rem', // 15
    '2.6rem', // 16
    '2.7rem', // 17
    '2.8rem', // 18
    '2.9rem', // 19
    '3.1rem', // 20
    '3.2rem', // 21
    '3.3rem', // 22
    '3.4rem', // 23
    '3.5rem', // 24
    '3.6rem', // 25
    '3.7rem', // 26
    '3.8rem', // 27
    '3.9rem', // 28
    '4rem', // 29
    '4.5rem', // 30
    '5rem', // 31
    '5.5rem', // 32
  ],
  fontWeights: [
    'normal', // 0
    'bold', // 1
    100, // 2
    200, // 3
    300, // 4
    400, // 5
    500, // 6
    600, // 7
    700, // 8
    800, // 9
    900, // 10
  ],
  colors: {
    // sun
    sun10: '#FFFEE7',
    sun20: '#FFFAC4',
    sun30: '#FEF69C',
    sun40: '#FDF174',
    sun50: '#FBEC53',
    sun60: '#FFEB00',
    sun70: '#FFDC35',
    sun80: '#FEC42C',
    sun90: '#FD9C35',
    sun100: '#F98214',

    // grass
    grass10: '#DFFFD7',
    grass20: '#C9FFB0',
    grass30: '#A5E696',
    grass40: '#5EDE50',
    grass50: '#55D342',
    grass60: '#1DCA0A',
    grass70: '#17B505',
    grass80: '#43A047',
    grass90: '#009100',
    grass100: '#006E00',

    // rose
    rose10: '#FFEBEE',
    rose20: '#FFDBDB',
    rose30: '#FFCDD2',
    rose40: '#FFA0A0',
    rose50: '#E57373',
    rose60: '#EF5350',
    rose70: '#F44336',
    rose80: '#E53935',
    rose90: '#C62828',
    rose100: '#B71C1C',

    // berry
    berry10: '#edf3ff',
    berry20: '#c8dbff',
    berry30: '#9dbfff',
    berry40: '#6fa1ff',
    berry50: '#4385ff',
    berry60: '#1a6aff',
    berry70: '#165ad9',
    berry80: '#124bb5',
    berry90: '#0f3c91',
    berry100: '#0c3073',

    // blue
    blue10: '#E2E9F7',
    blue20: '#C5CAE8',
    blue30: '#9EA8D8',
    blue40: '#7886C8',
    blue50: '#5B6CBC',
    blue60: '#3E52B1',
    blue70: '#384AA7',
    blue80: '#2F409B',
    blue90: '#27368F',
    blue100: '#00036D',

    // ash
    ash10: '#FFFFFF',
    ash20: '#F5F5F7',
    ash30: '#F0F1F8',
    ash40: '#EBECF0',
    ash50: '#D2D3D9',
    ash60: '#A5A6AC',
    ash70: '#7B7C82',
    ash80: '#67686D',
    ash90: '#47484E',
    ash100: '#26272C',

    // chart
    chart1: '#2962FF',
    chart2: '#FF6D00',
    chart3: '#AA00FF',
    chart4: '#F23645',
    chart5: '#4CAF50',
    chart6: '#FFEB3B',
    chart7: '#FF4081',
    chart8: '#089981',
    chart9: '#9C27B0',
    chart10: '#673AB7',
    chart11: '#00BCD4',
    chart12: '#FBC02D',
    chart13: '#00E5FF',
    chart14: '#00C853',
    chart15: '#E91E63',
    chart16: '#82B1FF',
    chart17: '#FF5252',
    chart18: '#FAA1A4',
    chart19: '#F48FB1',
    chart20: '#70CCBD',

    // rgba colors
    black25: 'rgba(0, 0, 0, 0.25)',
    black45: 'rgba(0, 0, 0, 0.45)',
    white12: 'rgba(255, 255, 255, 0.12)',
    white70: 'rgba(255, 255, 255, 0.7)',
    berry60_10: 'rgba(26, 106, 255, 0.10)',
    chart3_10: 'rgba(170, 0, 255, 0.1)',
    chart10_10: 'rgba(103, 58, 183, 0.1)',

    // other colors
    aquamarine: '#1DE9B6',
    mint: '#18C297',
    tropicalIndigo: '#A08EFF',
    oxfordBlue: '#001C34',
    yaleBlue: '#003C6D',
    darkPurple: '#281C2D',
    gunmetal: '#1c2722',
    ghostWhite: '#f7f7fc',
    navajoWhite: '#FFE1B9',
    burntOrange: '#B75800',
    seaSalt: '#FAFAFC',
    eminence: '#7D3198',
    palePurple: '#ECDEF3',
  },
  borders: [
    '1px solid', // 0
    '1px dashed', // 1
    '1px dotted', // 2
    '2px solid', // 3
    '2px dashed', // 4
    '2px dotted', // 5
    '3px solid', // 6
    '3px dashed', // 7
    '3px dotted', // 8
  ],
  shadows: {
    input: '0px 0px 0px 2px rgba(24, 144, 255, 0.2)',
    box: '0px 5px 15px rgba(0, 0, 0, 0.15)',
    header: '0px 5px 10px rgba(0, 0, 0, 0.05)',
    search: '0px 4px 6px rgba(0, 0, 0, 0.12)',
    navigator: '0px 16px 32px rgba(15, 24, 66, 0.1)',
    tab: '0px 10px 14px rgba(0, 0, 0, 0.05)',
    pay: '0px -5px 10px 0px rgba(0, 0, 0, 0.05)',
  },

  space: [
    scale(0), // 0
    scale(1), // 1
    scale(2), // 2
    scale(4), // 3
    scale(6), // 4
    scale(8), // 5
    scale(10), // 6
    scale(12), // 7
    scale(16), // 8
    scale(20), // 9
    scale(24), // 10
    scale(28), // 11
    scale(32), // 12
    scale(36), // 13
    scale(40), // 14
    scale(44), // 15
    scale(48), // 16
    scale(52), // 17
    scale(56), // 18
    scale(60), // 19
    scale(64), // 20
    scale(80), // 21
    scale(96), // 22
    scale(112), // 23
    scale(128), // 24
    scale(144), // 25
    scale(160), // 26
    scale(176), // 27
    scale(192), // 28
    scale(208), // 29
    scale(224), // 30
    scale(240), // 31
    scale(256), // 32
  ],
  sizes: [
    scale(0), // 0
    scale(1), // 1
    scale(2), // 2
    scale(4), // 3
    scale(6), // 4
    scale(8), // 5
    scale(10), // 6
    scale(12), // 7
    scale(16), // 8
    scale(20), // 9
    scale(24), // 10
    scale(28), // 11
    scale(32), // 12
    scale(36), // 13
    scale(40), // 14
    scale(44), // 15
    scale(48), // 16
    scale(52), // 17
    scale(56), // 18
    scale(60), // 19
    scale(64), // 20
    scale(80), // 21
    scale(96), // 22
    scale(112), // 23
    scale(128), // 24
    scale(144), // 25
    scale(160), // 26
    scale(176), // 27
    scale(192), // 28
    scale(208), // 29
    scale(224), // 30
    scale(240), // 31
    scale(256), // 32
  ],
  zIndices: [
    -1, // 0
    0, // 1
    1, // 2
    2, // 3
    3, // 4
    4, // 5
    5, // 6
    10, // 7
    100, // 8
    101, // 9
    102, // 10
    103, // 11
    104, // 12
    105, // 13
    1000, // 14
    1001, // 15
    1002, // 16
    1003, // 17
    1004, // 18
    1005, // 19
    2000, // 20
  ],
};

export default theme;
